/**
 * Platform/form-factor detection for the "Add to Home Screen" invitation.
 *
 * Installing a PWA is the one place where feature detection is not enough:
 * the *instructions* differ per OS and per browser chrome, and the browsers
 * that need instructions (iOS/iPadOS WebKit) expose no API at all for it. So
 * this module reads the user agent on purpose, and keeps that reading
 * confined here — every other module consumes the two enums below.
 *
 * The form factor drives placement rather than wording: on a phone the
 * browser chrome the visitor must reach is at the BOTTOM of the screen
 * (iOS Safari's tab bar), so the invitation is a bottom sheet; on a tablet
 * that chrome is a top toolbar, so it is a card anchored under it.
 */

/** OS family, as far as the install gesture is concerned. */
export type InstallPlatform = 'ios' | 'ipados' | 'android' | 'other'

/** Drives where the invitation is anchored, not what it says. */
export type InstallFormFactor = 'phone' | 'tablet' | 'desktop'

/** iPadOS 13+ reports a desktop Safari UA; only the touch points give it away. */
function isTouchMac(browserNavigator: Navigator): boolean {
  return /Macintosh/.test(browserNavigator.userAgent) && browserNavigator.maxTouchPoints > 1
}

export function detectInstallPlatform(browserWindow: Window = window): InstallPlatform {
  const userAgent = browserWindow.navigator.userAgent

  if (/iPad/.test(userAgent) || isTouchMac(browserWindow.navigator)) return 'ipados'
  if (/iPhone|iPod/.test(userAgent)) return 'ios'
  if (/Android/.test(userAgent)) return 'android'

  return 'other'
}

export function detectInstallFormFactor(browserWindow: Window = window): InstallFormFactor {
  const platform = detectInstallPlatform(browserWindow)

  if (platform === 'ipados') return 'tablet'
  if (platform === 'ios') return 'phone'
  // Android's own convention: a tablet build drops the `Mobile` token.
  if (platform === 'android') {
    return /Mobile/.test(browserWindow.navigator.userAgent) ? 'phone' : 'tablet'
  }

  return 'desktop'
}

/**
 * True when the page is already running as an installed app — the standalone
 * display mode on every engine, plus the legacy `navigator.standalone` flag
 * that iOS still is the only reporter of.
 */
export function isAppInstalled(browserWindow: Window = window): boolean {
  // Non-standard and absent from `Navigator`, so it is read reflectively
  // rather than asserted onto the type.
  const legacyIosFlag: unknown = Reflect.get(browserWindow.navigator, 'standalone')
  if (legacyIosFlag === true) return true

  if (typeof browserWindow.matchMedia !== 'function') return false

  return ['standalone', 'fullscreen', 'minimal-ui'].some(
    (mode) => browserWindow.matchMedia(`(display-mode: ${mode})`).matches,
  )
}
